import React, { useRef, useState, useEffect } from 'react';
import axios from 'axios';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';

// Generic multi-file uploader using presigned URLs
export default function FileUpload({ folder = 'misc', existing = [], onChange, token, maxFiles = 10, maxSizeMB = 250 }) {
    const fileInput = useRef();
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');

    const normalizeExisting = (e) => {
        if (Array.isArray(e)) return e;
        if (!e) return [];
        if (typeof e === 'string') {
            try {
                const parsed = JSON.parse(e);
                return Array.isArray(parsed) ? parsed : [];
            } catch (ex) {
                return [];
            }
        }
        return [];
    };

    const [files, setFiles] = useState(() => normalizeExisting(existing)); // array of {name, url}

    useEffect(() => { setFiles(normalizeExisting(existing)); }, [existing]);

    const handleFileSelect = async (e) => {
        setError('');
        const picked = Array.from(e.target.files || []);
        if (picked.length === 0) return;
        if (files.length + picked.length > maxFiles) {
            setError(`Maximum ${maxFiles} files allowed`);
            return;
        }
        // validate sizes
        for (const f of picked) {
            if (f.size > maxSizeMB * 1024 * 1024) {
                setError(`File ${f.name} exceeds ${maxSizeMB}MB limit`);
                return;
            }
        }
        setUploading(true);
        try {
            for (const f of picked) {
                // request presigned url
                const { data } = await axios.post('/api/collab/ilg/file-upload-url', {
                    fileName: f.name,
                    contentType: f.type || 'application/octet-stream',
                    folder
                }, {
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
                });
                const { uploadUrl, s3Url } = data;
                await axios.put(uploadUrl, f, {
                    headers: { 'Content-Type': f.type || 'application/octet-stream' }
                });
                // use functional update to avoid stale closure
                setFiles((prev) => {
                    const next = [...prev, { name: f.name, url: s3Url }];
                    if (onChange) onChange(next);
                    return next;
                });
                // small delay yield to allow state batching (not required but helpful)
                await new Promise((r) => setTimeout(r, 0));
            }
        } catch (err) {
            setError(err.message || 'Upload failed');
        } finally {
            setUploading(false);
            // reset input
            if (fileInput.current) fileInput.current.value = null;
        }
    };

    const handleDelete = async (file) => {
        setError('');
        setUploading(true);
        try {
            await axios.post('/api/collab/ilg/file-delete', { s3Url: file.url }, {
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
            });
            setFiles((prev) => {
                const next = prev.filter((f) => f.url !== file.url);
                if (onChange) onChange(next);
                return next;
            });
        } catch (err) {
            setError(err.message || 'Delete failed');
        } finally {
            setUploading(false);
        }
    };

    return (
        <div>
            <input type="file" ref={fileInput} style={{ display: 'none' }} multiple onChange={handleFileSelect} />
            <Button variant="outlined" onClick={() => fileInput.current && fileInput.current.click()} disabled={uploading || files.length >= maxFiles}>
                {uploading ? <CircularProgress size={18} /> : `Upload Files (${files.length}/${maxFiles})`}
            </Button>
            <div style={{ marginTop: 8 }}>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                    {files.map(f => (
                        <Chip key={f.url} label={f.name} onDelete={() => handleDelete(f)} sx={{ maxWidth: 300, wordBreak: 'break-word' }} />
                    ))}
                </Stack>
            </div>
            {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
        </div>
    );
}
